import moment from 'moment';
import { PrimeNGSeverity } from '../../../shared-module/shared/types/primeng-severity.type';
import { Trigger } from '../../entities/trigger/trigger';

const KNOWN_TRIGGER_STATES = ['NONE', 'NORMAL', 'PAUSED', 'COMPLETE', 'ERROR', 'BLOCKED'];

export function triggerStateLabelKey(state: string | null | undefined): string {
  const key = KNOWN_TRIGGER_STATES.includes(state ?? '') ? state : 'NONE';
  return `core.jobAdmin.triggerState.${key}`;
}

export function triggerStateSeverity(state: string | null | undefined): PrimeNGSeverity {
  switch (state) {
    case 'NORMAL':
      return 'success';
    case 'PAUSED':
      return 'warning';
    case 'ERROR':
      return 'danger';
    case 'BLOCKED':
      return 'danger';
    case 'COMPLETE':
      return 'info';
    default:
      return 'secondary';
  }
}

/** Rangfolge schlechtester zuerst. COMPLETE und NONE gelten gleich (kein Alarm, kein Normalfall). */
const TRIGGER_STATE_RANK: Record<string, number> = {
  ERROR: 0,
  BLOCKED: 1,
  PAUSED: 2,
  COMPLETE: 3,
  NONE: 3,
  NORMAL: 4
};

/**
 * Schlechtester Trigger-Zustand eines Jobs, fuer die Status-Spalte der Job-Liste (Spec §4.3, deckt
 * Issue-Punkt 4 "Warnings bei Jobs mit Status ERROR" ab). Ein Job ohne Trigger gilt als NONE.
 */
export function worstTriggerState(triggers: Trigger[]): string {
  if (!triggers || triggers.length === 0) {
    return 'NONE';
  }
  return triggers.reduce((worst, current) => {
    const worstRank = TRIGGER_STATE_RANK[worst] ?? TRIGGER_STATE_RANK.NONE;
    const currentRank = TRIGGER_STATE_RANK[current.triggerState] ?? TRIGGER_STATE_RANK.NONE;
    return currentRank < worstRank ? current.triggerState : worst;
  }, 'NORMAL');
}

/**
 * Kleinstes kuenftiges nextFireTime ueber alle Trigger eines Jobs (Spec §4.3). Trigger ohne
 * kuenftige Ausfuehrung (null oder vergangen) zaehlen nicht mit.
 */
export function nextJobExecution(triggers: Trigger[]): string | null {
  const now = moment();
  const future = (triggers ?? [])
    .map(t => t.nextFireTime)
    .filter((iso): iso is string => !!iso && moment(iso).isAfter(now));

  if (future.length === 0) {
    return null;
  }
  return future.reduce((min, iso) => (moment(iso).isBefore(moment(min)) ? iso : min));
}

/**
 * "in X Min/Std/Tagen" fuer einen zukuenftigen ISO-Zeitpunkt. formatRelativeTime
 * (core/pipes/relative-time.pipe.ts) ist nur fuer die Vergangenheit korrekt: bei einem zukuenftigen
 * Wert wird der Diff negativ, "-5 < 1" ist wahr, und die Funktion liefert faelschlich "gerade eben".
 * Vergangene oder fehlende Werte liefern hier bewusst "–", nicht "gerade eben".
 */
export function formatFutureRelativeTime(iso: string | null | undefined): string {
  if (!iso) {
    return '–';
  }
  const target = moment(iso);
  const now = moment();
  const minutes = target.diff(now, 'minutes');
  if (minutes <= 0) {
    return '–';
  }
  if (minutes < 60) {
    return `in ${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`;
  }
  const hours = target.diff(now, 'hours');
  if (hours < 24) {
    return `in ${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
  }
  const days = target.diff(now, 'days');
  return `in ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
}
